import { useEffect, useRef, useState } from 'react';
import { EllipsisVerticalIcon } from 'lucide-react';
import { Box, IconButton, Menu, useToken } from '@chakra-ui/react';
import { Medication, PrescriptionMedicationStatus } from 'api/types';
import useUpdateUserMedication from 'api/healthCoach/mutations/useUpdateUserMedication';
import { useDashboardPageState } from 'hooks/useDashboardPageState';

type MedicationMenuProps = {
  medication: Medication;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export const MedicationMenu = ({
  medication,
  isOpen,
  onOpenChange,
}: MedicationMenuProps) => {
  const {
    state: { user },
  } = useDashboardPageState();
  const [steel] = useToken('colors', ['steel']);
  const isActive = medication.status === PrescriptionMedicationStatus.Active;
  const { mutation: updateMedicationMutation } = useUpdateUserMedication();
  const containerRef = useRef<HTMLDivElement>(null);
  const [openUpward, setOpenUpward] = useState(false);

  // Estimated height of the menu content, used to decide whether it fits
  // below the trigger before the actual content is measured.
  const ESTIMATED_MENU_HEIGHT = 60;

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const { bottom } = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom;
    setOpenUpward(spaceBelow < ESTIMATED_MENU_HEIGHT);
  }, [isOpen]);

  const updateMedication = () => {
    if (!user?.id) return;

    updateMedicationMutation.mutate({
      userId: user.id,
      medicationId: medication.id,
      active: !isActive,
    });
  };

  return (
    <Box
      ref={containerRef}
      position={'relative'}
      zIndex={isOpen ? 1 : 'auto'}
      onClick={(e) => e.stopPropagation()}
    >
      <Menu.Root
        open={isOpen}
        onOpenChange={(details) => onOpenChange(details.open)}
      >
        <Menu.Trigger asChild>
          <IconButton variant={'ghost'}>
            <EllipsisVerticalIcon color={steel} size={16} />
          </IconButton>
        </Menu.Trigger>
        <Menu.Positioner
          style={
            {
              position: 'absolute',
              bottom: openUpward ? '100%' : 'auto',
              top: openUpward ? 'auto' : '100%',
              right: 0,
              left: 'auto',
              marginTop: '8px',
              marginBottom: '8px',
              transform: 'none',
            } as const
          }
        >
          <Menu.Content>
            <Menu.Item
              fontSize={'xs'}
              color={isActive && 'fuschia'}
              onSelect={updateMedication}
              value="no-longer-taking"
              _highlighted={{ bg: 'mint' }}
            >
              {isActive ? 'No longer taking' : 'Move back to current'}
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
    </Box>
  );
};

export default MedicationMenu;
